import {
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	REPELL_BOUNDARY,
	REPELL_HAS_RETURN,
	REPELL_POSITION,
	REPELL_PUSH,
	REPELL_VECTOR
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

/**
 * Repell kernel — verbatim port of ParticleRepell.tick(), all four types
 * ('POSITION' mode — the class's `_mode` is never mutated, so the ANCHOR
 * branch is dead code and not ported).
 *
 * Bug-compatible notes (do not "fix" without a design decision):
 *  - PUSH ignores `_power`: the reference's first target writes (center +
 *    cos*power) are overwritten two lines later by the live math
 *    (move = 50 * r4 off the pure parallax target) — only the live math is
 *    ported. repellPower stays a lane because the other types use it.
 *  - PUSH rebuilds targets from ref.position.absX/absY = the PURE parallax
 *    targets (pTx/pTy lanes) — the mouse-parallax addition to tx/ty is
 *    discarded inside the radius, exactly like the reference.
 *  - Distance tests use the CURRENT values (animatedProps.x/y — 'POSITION'
 *    mode), i.e. the x/y lanes before this frame's integrate.
 *  - POSITION (stage 5, the swirl): nudges the REL position lanes
 *    (relX/relY — the reference's position.x/y setters) by cos*power /
 *    sin*(power*3) and squashes targetScale to saved*0.2 while inside the
 *    radius; when returnTo is set it additionally lerps the rel position
 *    home EVERY processed tick (inside or out) and then returns before the
 *    BOUNDARY retarget block, like the reference.
 *  - VECTOR (stage 5, the drop machine): feeds the physics vectors —
 *    particlePhysics.repell(cos*((power/2)*(2*dt)), sin*(power*3*(2*dt))).
 *  - The BOUNDARY retarget block runs EVERY tick (it sits after the radius
 *    if/else in the reference), snapping targets onto the power-radius ring.
 *  - sqrt/atan2 only run inside the radius (the reference computes sqrt
 *    unconditionally, but the check `d <= r` is output-identical to
 *    `d² <= r²` — the only deviation ported, per the design's
 *    "optimise only where output-identical" rule).
 *
 * The reference also skips when sprite.visible === false (covered by the
 * FLAG_ACTIVE gate — every home caller pairs visibility with active) and
 * supports testAtCount decimation (home configs use the default 1 = every
 * tick); the decimation arrives when a department needs it.
 */
export function repellKernel(
	core: ParticleCore,
	range: PoolRange,
	centerX: number,
	centerY: number,
	deltaTime: number
): void {
	const mask = core.behaviourMask;
	const flags = core.stateFlags;

	for (let i = range.start; i < range.end; i++) {
		if (!(flags[i] & FLAG_ACTIVE) || !(mask[i] & BEHAVIOUR_REPELL)) {
			continue;
		}

		const type = core.repellType[i];
		const radius = core.repellRadius[i];
		const a = core.x[i] - centerX;
		const b = core.y[i] - centerY;
		const d2 = a * a + b * b;

		if (d2 <= radius * radius) {
			const radians = Math.atan2(b, a);

			if (type === REPELL_PUSH) {
				const distance = Math.sqrt(d2);
				//0..1 through the radius -> 1 at the midpoint, 0 at both ends:
				const r2 = distance / radius - 0.5;
				const r3 = 0.5 - Math.abs(r2);
				const r4 = r3 / 0.5;
				const move = 50 * r4;
				core.tx[i] = core.pTx[i] + Math.cos(radians) * move;
				core.ty[i] = core.pTy[i] + Math.sin(radians) * move;
			} else if (type === REPELL_BOUNDARY) {
				//Harsh: constrain the VALUE to the power ring immediately:
				core.x[i] = centerX + Math.cos(radians) * core.repellPower[i];
				core.y[i] = centerY + Math.sin(radians) * core.repellPower[i];
			} else if (type === REPELL_POSITION) {
				core.relX[i] += Math.cos(radians) * core.repellPower[i];
				core.relY[i] += Math.sin(radians) * (core.repellPower[i] * 3);
				core.tScale[i] = core.tScaleSaved[i] * 0.2;
			} else if (type === REPELL_VECTOR) {
				core.vx[i] += Math.cos(radians) * ((core.repellPower[i] / 2) * (2 * deltaTime));
				core.vy[i] += Math.sin(radians) * (core.repellPower[i] * 3 * (2 * deltaTime));
			}
		} else if (core.tScaleSaved[i]) {
			core.tScale[i] = core.tScaleSaved[i];
		}

		//returnTo home-pull (POSITION only) — every processed tick, then the
		//reference returns before the BOUNDARY retarget:
		if (core.repellFlags[i] & REPELL_HAS_RETURN && type === REPELL_POSITION) {
			core.relX[i] += (core.repellReturnX[i] - core.relX[i]) * core.repellReturnPowX[i];
			core.relY[i] += (core.repellReturnY[i] - core.relY[i]) * core.repellReturnPowY[i];
			continue;
		}

		//BOUNDARY retargets every tick (reference lines 170-185):
		if (type === REPELL_BOUNDARY) {
			const ta = core.tx[i] - centerX;
			const tb = core.ty[i] - centerY;
			const targetDistance = Math.sqrt(ta * ta + tb * tb);

			if (targetDistance) {
				const targetRadians = Math.atan2(tb, ta);
				core.tx[i] = centerX + Math.cos(targetRadians) * core.repellPower[i];
				core.ty[i] = centerY + Math.sin(targetRadians) * core.repellPower[i];
			}
		}
	}
}
